/**
 * Task enum → human label.
 *
 * The API hands the Tasks page raw enum values (`in_progress`,
 * `linkedin_connect`, `book_meeting`) and the page used to drop several of them
 * straight into the DOM behind a CSS `capitalize`. That only *looks* fixed for
 * single-word values: `capitalize` uppercases the first letter of each word and
 * leaves the underscore alone, so `urgent` renders "Urgent" while `in_progress`
 * renders "In_progress" — the bug is invisible until the backend adds a
 * multi-word value.
 *
 * The filter dropdowns already carry copywritten labels ("In progress",
 * "LinkedIn connect", "Follow-up"), so those same lists are the single source of
 * truth for every user-visible spelling of a task field. Anything the backend
 * adds later that isn't in a list degrades through `humanizeEnum` to a readable
 * "Some new status" instead of leaking `some_new_status` into the UI.
 */

export interface EnumOption {
  value: string
  label: string
}

/** Kept mutable (not `as const`) so these lists drop straight into `Select`'s
 *  `options` prop, which takes a mutable `SelectOption[]`. */
export const TASK_TYPE_OPTIONS: EnumOption[] = [
  { value: '',                  label: 'All types' },
  { value: 'call',              label: 'Call' },
  { value: 'linkedin_connect',  label: 'LinkedIn connect' },
  { value: 'linkedin_message',  label: 'LinkedIn message' },
  { value: 'manual_email',      label: 'Manual email' },
  { value: 'research',          label: 'Research' },
  { value: 'follow_up',         label: 'Follow-up' },
  { value: 'book_meeting',      label: 'Book meeting' },
  { value: 'custom',            label: 'Custom' },
]

export const STATUS_OPTIONS: EnumOption[] = [
  { value: '',             label: 'All statuses' },
  { value: 'pending',      label: 'Pending' },
  { value: 'in_progress',  label: 'In progress' },
  { value: 'completed',    label: 'Completed' },
  { value: 'cancelled',    label: 'Cancelled' },
]

export const PRIORITY_OPTIONS: EnumOption[] = [
  { value: '',        label: 'All priorities' },
  { value: 'low',     label: 'Low' },
  { value: 'medium',  label: 'Medium' },
  { value: 'high',    label: 'High' },
  { value: 'urgent',  label: 'Urgent' },
]

/** Meeting providers we can name. The brand dot colour lives with the chip in
 *  the page (presentation); only the wording belongs here. */
export const MEETING_PROVIDER_OPTIONS: EnumOption[] = [
  { value: 'outlook',   label: 'Outlook' },
  { value: 'zoom',      label: 'Zoom' },
  { value: 'calendly',  label: 'Calendly' },
]

/** Per-row, editable status values (no "All statuses" entry) — used by the
 *  inline Status dropdown so a user can change a task's status manually. */
export const STATUS_EDIT_OPTIONS: EnumOption[] = STATUS_OPTIONS.filter(o => o.value !== '')

/**
 * Last-resort spelling for an enum value no option list knows about: strip the
 * separators and read it as a sentence, so a value the backend ships tomorrow
 * shows up as "Some new status" rather than `some_new_status`. Sentence case
 * (not Title Case) matches the hand-written labels above, which capitalise only
 * the first word.
 */
export function humanizeEnum(value: string | null | undefined): string {
  const words = String(value ?? '').replace(/[_-]+/g, ' ').trim().replace(/\s+/g, ' ')
  if (!words) return ''
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase()
}

/**
 * Label for `value` from `options`, humanized when absent.
 *
 * The empty-value entries ("All statuses") are filter placeholders, never a
 * task's own value, so an empty/missing field resolves to `''` — the caller
 * decides what a blank field looks like (an em dash, usually) rather than
 * getting "All statuses" stamped on a row.
 */
export function optionLabel(options: EnumOption[], value: string | null | undefined): string {
  const raw = String(value ?? '')
  if (!raw) return ''
  const hit = options.find(o => o.value === raw && o.value !== '')
  return hit ? hit.label : humanizeEnum(raw)
}

export const taskTypeLabel = (value: string | null | undefined): string =>
  optionLabel(TASK_TYPE_OPTIONS, value)

export const taskStatusLabel = (value: string | null | undefined): string =>
  optionLabel(STATUS_OPTIONS, value)

export const taskPriorityLabel = (value: string | null | undefined): string =>
  optionLabel(PRIORITY_OPTIONS, value)

export const meetingProviderLabel = (value: string | null | undefined): string =>
  optionLabel(MEETING_PROVIDER_OPTIONS, value)

/**
 * Options for one row's inline Status dropdown.
 *
 * `Select` falls back to its placeholder when `value` matches no option, so a
 * status the backend adds later would render an empty-looking "Select…" trigger
 * on a row that *does* have a status. Appending the unknown value (humanized)
 * keeps the row truthful and still lets the user move the task to a status we
 * do know.
 */
export function statusOptionsFor(status: string | null | undefined): EnumOption[] {
  const raw = String(status ?? '')
  if (!raw || STATUS_EDIT_OPTIONS.some(o => o.value === raw)) return STATUS_EDIT_OPTIONS
  return [...STATUS_EDIT_OPTIONS, { value: raw, label: humanizeEnum(raw) }]
}
